// pages/blog/[url_slug].jsx   (MERGED: handles BOTH posts and tags at /blog/:url_slug)
import { Global, Collection } from "@sourceflow-uk/sourceflow-sdk";
import SeoHead from "../../components/SeoHead";
import { Helmet } from "react-helmet";
import LatestNews from "../../components/LatestNews";
import { Container } from "reactstrap";
import NewBlogDetails from "../../components/NewBlogDetails";
import NewSubBanner from "../../components/NewSubBanner";
import NewColumnWithForm from "../../components/NewColumnWithForm";
import NewBlogDetails2 from "../../components/NewBlogDetails2";

import MainBanner from "../../components/MainBanner";
import AllBlogs from "../../components/AllBlogs";
import NewFeaturedBlogs from "../../components/NewFeaturedBlogs";

// ---------- helpers ----------
function hasTag(item, tagName) {
  if (!item?.tag || !tagName) return false;
  const t = item.tag;
  const target = String(tagName).toLowerCase();

  // arrays of strings
  if (Array.isArray(t)) return t.some(x => String(x).toLowerCase() === target);

  // localized { en: [...] } or { en: '...' }
  if (Array.isArray(t?.en)) return t.en.some(x => String(x).toLowerCase() === target);
  if (typeof t?.en === "string") return String(t.en).toLowerCase() === target;

  // single string
  if (typeof t === "string") return t.toLowerCase() === target;

  return false;
}

function PostView({ global, page, all_news, related_news }) {
  const breadcrumbList = [
    { "@type": "ListItem", name: "SRG", position: 1, item: { "@type": "Thing", "@id": `https://www.srgtalent.com/` } },
    { "@type": "ListItem", position: 2, name: "Blog", item: { "@type": "Thing", "@id": `https://www.srgtalent.com/blog` } },
  ];

  return (
    <>
      <SeoHead
        page_name={`${page.title} | SRG${page.tag.includes("News & Insights") ? " Insights" : "" }`}
        description={page.tag.includes("Case Study") ? `Discover insights from "${page.title}" and how SRG delivers effective solutions across Life Sciences.` : `Expert insights on ${page.title} from SRG, specialists in Life Sciences.`}
        canonical_link={`blog/${page.url_slug}`}
        og_image={page.image}
        og_img_alt={`${page.title}`}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: breadcrumbList })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            description: page?.short_description || "",
            isFamilyFriendly: "http://schema.org/True",
            image: page?.image || "",
            dateModified: page?.updated_at || "",
            datePublished: page?.created_at || "",
            name: page?.title || "",
            headline: page?.title || "",
            articleBody: page?.content ? page.content.replace(/<[^>]*>/g, "") : "",
            publisher: {
              "@type": "Organization",
              name: "SRG",
              sameAs: "https://www.srgtalent.com/",
              logo: { "@type": "ImageObject", url: "https://cdn.sourceflow.co.uk/otncy6ttisad8f91af1vjs6kh25s" },
            },
            author: { "@type": "Person", image: page?.author_image || "", name: page?.author || "" },
          })}
        </script>
      </Helmet>

      <NewSubBanner
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.main_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`Main Banner`}
        imageURL2={`/site-assets/svg/banner-overlay.svg`}
        imageALT2={`Overlay Main Banner`}
        displayImage1={false}
        editableContent={false}
        buttonLink={`${
          page.tag.includes("Case Study")
            ? "/blog/case-study"
            : page.tag.includes("Guides & Reports")
            ? "/blog/guides-and-reports"
            : "/blog"
        }`}
        buttonTitle={`${
          page.tag.includes("Case Study")
            ? "Back to Case Study"
            : page.tag.includes("Guides & Reports")
            ? "Back to Guides & Reports"
            : "Back to insights"
        }`}
        post={page}
        buttonLink2={`${page.tag.includes("Guides & Reports") && page.form_id ? "#download-form" : ""}`}
        buttonTitle2={`${page.tag.includes("Guides & Reports") && page.form_id ? "Download Report" : ""}`}
        ctaLink={page?.cta_link ?? null}
        ctaTitle={page?.cta_title ?? null}
      />

      {page.tag.includes("Guides & Reports") ||
      page.tag.includes("News & Insights") ||
      page.tag.includes("Case Study") ? (
        <NewBlogDetails
          global={global}
          pathPrefix={`insights_page.guides_and_reports.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`blog`}
          share_heading={`Share Report`}
          hubSpotForm={page.hubspot_form_id}
          className={`py-5 guides-and-reports-page`}
          formId={page.form_id}
          locale={``}
          successPageUrl={
            page.form_link ? page.form_link : page.form_attachment ? page.form_attachment : "/submission-success"
          }
        />
      ) : page.tag.includes("Case Study") ? (
        page.case_study_tabs?.length >= 1 && (
          <NewBlogDetails2 global={global} pathPrefix={`insights_page.new_blog_details_2`} page={page} />
        )
      ) : (
        <NewBlogDetails
          global={global}
          pathPrefix={`insights_page.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`blog`}
          locale={``}
        />
      )}

      {(page.tag.includes("Guides & Reports") || page.tag.includes("Case Study")) && all_news.length >= 1 ? (
        <>
          <Container><hr /></Container>
          <LatestNews
            global={global}
            pathPrefix={`${page.url_slug}.insights_page.guides_and_reports.latest_news`}
            customClass={`py-5 white-card`}
            post={
              page.tag.includes("Guides & Reports")
                ? all_news.filter((p) => hasTag(p, "Guides & Reports") && p.title !== page.title)
                : all_news.filter((p) => hasTag(p, "Case Study") && p.title !== page.title)
            }
            content={<h2>{page.tag.includes("Guides & Reports") ? "Other Guides and Reports" : "Other Case Studies"}</h2>}
            buttonLink={``}
            buttonTitle={``}
            content_align={`left`}
            footer_buttonLink={`/blog${page.tag.includes("Guides & Reports") ? "/guides-and-reports" : "/case-study"}`}
            footer_buttonLink_title={`View All`}
          />
        </>
      ) : null}

      <NewColumnWithForm
        global={global}
        pathPrefix={`insights_page.new_column_with_form`}
        customClass={`pb-5`}
        heading={`How can we help you?`}
        formId={`e1da7ff6-feb2-4696-93be-b43ec16b6009`}
        description={<p>Contact us if you are interested in finding out more about how we can help you.</p>}
        buttonLink={`/blog/`}
        buttonTitle={`View All Insights`}
      />
    </>
  );
}

function TagView({ global, page, all_news, featuredNews }) {
  return (
    <>
      <SeoHead
        page_name={`${page.title} | Science, Engineering & Life Science Recruitment, Jobs & Staffing | SRG`}
        canonical_link={`blog/${page.url_slug}`}
      />
      <MainBanner
        global={global}
        pathPrefix={`insights_page.main_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`Main Banner`}
        imageURL2={`/site-assets/images/main-banner-overlay.jpg`}
        imageALT2={`Overlay Main Banner`}
        editableContent={false}
        displayImage1={false}
      />
      <NewFeaturedBlogs
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.new_featured_blogs`}
        customClass={`py-5 grayGradientBg`}
        content={<h2>Featured {page.title}</h2>}
        post={featuredNews}
        socialPagePrefix={`blog/${page.url_slug}`}
      />
      <AllBlogs
        global={global}
        pathPrefix={`news_page.all_blogs`}
        customClass={`py-5 lightGreybg`}
        post={all_news}
        ParentNews={false}
        tagName={`${page.title}`}
        heading={`All ${page.title}`}
      />
      <NewColumnWithForm
        global={global}
        pathPrefix={`insights_page.new_column_with_form`}
        customClass={`pb-5`}
        heading={`How can we help you?`}
        formId={`e1da7ff6-feb2-4696-93be-b43ec16b6009`}
        description={<p>Contact us if you are interested in finding out more about how we can help you.</p>}
      />
    </>
  );
}

export default function BlogUnified(props) {
  return props.mode === "post" ? <PostView {...props} /> : <TagView {...props} />;
}

export async function getStaticProps({ params }) {
  const global = (await Global()).toJson();

  // 1) Try POST first
  let postPage = null;
  try {
    postPage = (await Collection("news")).getJsonItem(params.url_slug, "url_slug");
  } catch (_) {}

  if (postPage && postPage.url_slug) {
    const news = (await Collection("news"))
      .filter((post) => post.country?.en == "United Kingdom" || post.country?.en == "All")
      .filter((post) => Array.isArray(post.tag?.en) && post.tag.en.some((t) => postPage.tag?.includes(t)));

    const all_news = news.orderBy("order").getItems().slice(0, 8);
    const related_news = news.orderBy("order").getItems().slice(0, 2);

    return {
      props: {
        global,
        mode: "post",
        page: postPage,
        all_news,
        related_news,
      },
    };
  }

  // 2) Fallback to TAG — sector/topic tags and content types live in separate collections
  let tagPage = null;
  try {
    tagPage = (await Collection("news_tag")).getJsonItem(params.url_slug, "url_slug");
  } catch (_) {}

  if (!tagPage) {
    try {
      tagPage = (await Collection("news_content_type")).getJsonItem(params.url_slug, "url_slug");
    } catch (_) {}
  }

  if (!tagPage) return { notFound: true };

  const tagName = tagPage.title;

  const news = (await Collection("news"))
    .filter((post) => post.country?.en == "United Kingdom" || post.country?.en == "All")
    .filter((post) => Array.isArray(post.tag?.en) && post.tag.en.includes(tagName));

  const allItemsSorted = news
    .orderBy((n) => Date.parse(n.date?.en), "desc")
    .getItems();

  const featuredNews = allItemsSorted.filter((item) => hasTag(item, tagName) && item.featured === true);

  const all_news = allItemsSorted.filter((item) => item.id !== featuredNews[0]?.id);

  return {
    props: {
      global,
      mode: "tag",
      page: tagPage,
      all_news,
      featuredNews,
    },
  };
}

export async function getStaticPaths() {
  // Post URLs
  const postPaths = (await Collection("news"))
    .filter((post) => post.country?.en == "United Kingdom" || post.country?.en == "All")
    .getPaths("url_slug");

  // Tag URLs (exclude career-advice as before)
  const tagPaths = (await Collection("news_tag"))
    .filter((tag) => tag.url_slug !== "career-advice")
    .getPaths("url_slug");

  // Content type URLs
  const contentTypePaths = (await Collection("news_content_type")).getPaths("url_slug");

  return {
    paths: [...postPaths, ...tagPaths, ...contentTypePaths],
    fallback: false,
  };
}
